import React, { useState, useEffect } from 'react';
import { Button, Card, H3, FormGroup, InputGroup, Intent } from '@blueprintjs/core';
import { useNavigate } from 'react-router-dom';
import CurlNoiseBackground from '../components/onboarding/CurlNoiseBackground';
import { useAppStore } from '../stores/UnifiedStoreProvider';
import axios from 'axios';
import './OnboardingProfile.css';

/**
 * OnboardingProfile component for collecting user information
 * during the onboarding process - standalone implementation
 */
const UserProfile: React.FC = () => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const appStore = useAppStore();
  const { auth } = appStore;
  const user = auth.user;
  
  // Check if user already has profile data
  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    
    // Pre-fill if user already has name
    if (user.name) {
      const nameParts = user.name.split(' ');
      setFirstName(nameParts[0] || '');
      setLastName(nameParts.slice(1).join(' ') || '');
    }
  }, [user, navigate]);

  const handleSubmit = async () => {
    if (!firstName.trim() || !lastName.trim()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Update user profile via backend
      const response = await axios.patch(
        `${import.meta.env.VITE_BACKEND_URL || 'http://localhost:4011'}/users/profile`,
        {
          firstName,
          lastName,
          jobTitle,
        },
        {
          headers: {
            Authorization: `Bearer ${auth.accessToken}`,
          },
        }
      );

      // Update local auth store
      if (response.data) {
        auth.setUser({
          ...user!,
          name: `${firstName} ${lastName}`,
        });
      }

      console.log('✅ Profile updated successfully');
      
      // Navigate to main app
      const orgSlug = auth.organization?.slug || 'default';
      navigate(`/${orgSlug}/mindmap`);
    } catch (error) {
      console.error('❌ Error updating profile:', error);
      // Still navigate even if profile update fails
      const orgSlug = auth.organization?.slug || 'default';
      navigate(`/${orgSlug}/mindmap`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkip = () => {
    const orgSlug = auth.organization?.slug || 'default';
    navigate(`/${orgSlug}/mindmap`);
  };

  return (
    <div className="onboarding-container">
      <CurlNoiseBackground />
      <div className="onboarding-content">
        <Card elevation={2} className="onboarding-card">
          <div className="onboarding-header">
            <H3>Complete Your Profile</H3>
            <p className="onboarding-subtitle">
              Help your team recognize you by adding your name and role
            </p>
          </div>

          <div className="onboarding-form">
            <FormGroup label="First Name" labelInfo="(required)">
              <InputGroup
                large
                placeholder="Enter your first name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                disabled={isSubmitting}
              />
            </FormGroup>

            <FormGroup label="Last Name" labelInfo="(required)">
              <InputGroup
                large
                placeholder="Enter your last name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                disabled={isSubmitting}
              />
            </FormGroup>

            <FormGroup label="Job Title" labelInfo="(optional)">
              <InputGroup
                large
                placeholder="e.g. Software Engineer, Product Manager"
                value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
                disabled={isSubmitting}
              />
            </FormGroup>
          </div>

          <div className="onboarding-actions">
            <Button
              large
              intent={Intent.PRIMARY}
              text={isSubmitting ? "Saving..." : "Continue"}
              onClick={handleSubmit}
              disabled={!firstName.trim() || !lastName.trim() || isSubmitting}
              loading={isSubmitting}
            />
            <Button
              large
              minimal
              text="Skip for now"
              onClick={handleSkip}
              disabled={isSubmitting}
            />
          </div>
        </Card>
      </div>
    </div>
  );
};

export default UserProfile;